/**
 * Framework-agnostic mount coordinator.
 *
 * Orchestrates the per-component mount flow that every SDX
 * framework module shares:
 *
 *   1. Short-circuit if the container is already mounted or
 *      currently mid-mount (concurrent attach calls from
 *      overlapping behavior runs).
 *   2. Wait for the framework's Vite client + optional dev
 *      preamble so HMR runtime context is ready.
 *   3. Re-read settings (the Vite client's onerror handler may
 *      have cleared the dev_server field).
 *   4. Read the component's props from the container's props
 *      source.
 *   5. Resolve the component URL via the shared resolver.
 *   6. Load the framework runtime and the component module in
 *      parallel.
 *   7. Install the framework's rerender hook once per page
 *      (the adapter's hook is idempotent).
 *   8. Commit through the shared CommitScheduler (at most one
 *      adapter `mount()` per event-loop task, see
 *      commit-scheduler.ts) and register the native instance in
 *      the shared registries.
 *   9. On failure: retry once after a short delay, then fall
 *      back to the crash reporter.
 *
 * Every step that varies per framework (runtime loader,
 * preamble, mount impl, rerender hook) is a method on the
 * adapter. Every step that is identical across frameworks
 * (props, resolver, retry, crash report) lives here.
 */

import { resolveComponentPath } from './component-resolver';
import { readPropsFromContainer, PROPS_PENDING } from './props-reader';
import { reportMountCrash } from './crash-reporter';
import { CommitScheduler } from './commit-scheduler';
import type { FrameworkMountAdapter, FrameworkSettings } from './types';
import type { MountRegistry } from './mount-registry';

const RETRY_DELAY_MS = 200;
const MAX_RETRIES = 1;

interface ContainerWithFlags extends HTMLElement {
  _sdxMounting?: boolean;
}

export interface MountContext {
  componentMap: Record<string, string>;
  devServer: string | null;
}

interface ComponentModule {
  default?: unknown;
}

export class MountCoordinator<Instance, RuntimeModule> {
  private rerenderInstalled = false;

  private readonly commits = new CommitScheduler();

  constructor(
    private readonly adapter: FrameworkMountAdapter<Instance, RuntimeModule>,
    private readonly registry: MountRegistry<Instance>,
  ) {}

  async mount(container: HTMLElement, ctx: MountContext): Promise<void> {
    const typedContainer = container as ContainerWithFlags;
    if (
      container.dataset.sdxMounted
      || this.registry.mountedRoots.has(container)
      || typedContainer._sdxMounting
    ) {
      return;
    }
    typedContainer._sdxMounting = true;

    // The in-flight flag is a concurrency guard, not a permanent
    // marker: every exit path (success, early-return validation,
    // thrown exception) clears it so a later attachBehaviors pass
    // can retry. Retaining the flag after a successful mount used
    // to cause SPA navigation to skip re-mount: React reuses the
    // scaffold <div> across the nav and strips the previous
    // `data-sdx-mounted` attribute (it isn't in the JSX output),
    // so `dataset.sdxMounted` is gone while `_sdxMounting` is
    // still true. With this guard always cleared, the next attach
    // sees a mountable container and mounts the new widget.
    try {
      await this.adapter.ensurePreamble?.(ctx.devServer);
      await this.adapter.ensureDevClient(ctx.devServer);

      // Re-read settings in case the adapter's onerror path
      // cleared the dev server after the @vite/client load
      // failed. The subsequent runtime and component loads must
      // use the same devServer the resolver will see, otherwise
      // we end up fetching half from dev and half from prod.
      const settings: FrameworkSettings = this.adapter.readSettings();
      const devServer = settings.dev_server ?? null;

      const componentId = container.dataset.component;
      if (!componentId) {
        console.error(`${this.adapter.logPrefix} Container has no data-component attribute.`);
        return;
      }

      const props = readPropsFromContainer(container, componentId, this.adapter.logPrefix);
      if (props === PROPS_PENDING) {
        // Props arrive in memory from the scaffold's commit effect;
        // do not mount and set no mounted/error flag - the scaffold
        // nudges attachBehaviors once the payload is attached, and
        // the finally below clears the in-flight guard. The
        // diagnostic attribute makes a page whose app never hydrates
        // inspectable instead of silently unmounted.
        container.dataset.sdxPropsPending = 'true';
        return;
      }
      if (props === null) {
        container.dataset.sdxMounted = 'error';
        return;
      }

      const componentPath = resolveComponentPath(
        componentId,
        ctx.componentMap,
        devServer,
        settings,
        this.adapter.logPrefix,
      );
      if (!componentPath) {
        console.warn(`${this.adapter.logPrefix} No path for component: ${componentId}`);
        return;
      }

      try {
        const [runtime, componentModule] = await Promise.all([
          this.adapter.loadRuntime(devServer),
          import(/* @vite-ignore */ componentPath) as Promise<ComponentModule>,
        ]);

        const component = componentModule.default;
        if (!component) {
          throw new Error('Component module has no default export');
        }

        this.registry.loadedComponents.set(componentId, component);

        // Module loading above stays fully parallel; only the commit
        // is serialized, at most one component per event-loop task.
        await this.commits.schedule(() => {
          // Between load completion and this commit slot the
          // container may have left the DOM (SPA navigation, AJAX
          // replacement, scaffold innerHTML wipe) or been mounted by
          // another attach pass. A skipped commit resolves silently;
          // the finally below still clears the in-flight flag, so a
          // re-attached container mounts fresh on the next pass.
          if (!container.isConnected) return;
          if (
            container.dataset.sdxMounted
            || this.registry.mountedRoots.has(container)
          ) {
            return;
          }

          this.installRerenderHookOnce(runtime);
          const instance = this.adapter.mount({
            container,
            component,
            props,
            runtime,
            hydrate: container.dataset.sdxSsr !== undefined,
          });
          this.registry.mountedRoots.set(container, instance);
          container.dataset.sdxMounted = 'true';
          delete container.dataset.sdxPropsPending;
        });
      }
      catch (error) {
        this.handleFailure(container, componentId, error, ctx);
      }
    }
    finally {
      typedContainer._sdxMounting = false;
    }
  }

  unmount(container: HTMLElement): void {
    const instance = this.registry.mountedRoots.get(container);
    if (instance === undefined) return;
    this.adapter.unmount(instance);
    this.registry.mountedRoots.delete(container);
    delete container.dataset.sdxMounted;
  }

  /**
   * Drops the registry entry + mount flag without tearing down the
   * framework root.
   *
   * Used when a React-owned scaffold replaces its own innerHTML
   * (MountPointScaffold's `dangerouslySetInnerHTML` re-apply on
   * re-render with new component or props): the previous widget's
   * DOM is already gone, so calling `adapter.unmount(root)` would
   * have React's `root.unmount()` walk into a detached tree and
   * throw `NotFoundError: removeChild` asynchronously - an error
   * no try/catch can catch from the call site. This method skips
   * the framework `unmount` call entirely; the root instance is
   * just dropped from the registry so GC can reclaim it once
   * nothing else references it. The fresh content is then mounted
   * by the next attachBehaviors pass against the cleaned-up
   * container.
   */
  invalidateMount(container: HTMLElement): void {
    if (this.registry.mountedRoots.has(container)) {
      this.registry.mountedRoots.delete(container);
    }
    delete container.dataset.sdxMounted;
  }

  private installRerenderHookOnce(runtime: RuntimeModule): void {
    if (this.rerenderInstalled) return;
    this.adapter.registerRerenderHook?.(runtime, this.registry);
    this.rerenderInstalled = true;
  }

  private handleFailure(
    container: HTMLElement,
    componentId: string,
    error: unknown,
    ctx: MountContext,
  ): void {
    const retryAttr = container.dataset.sdxRetry;
    const retryCount = retryAttr ? parseInt(retryAttr, 10) : 0;
    const message = error instanceof Error ? error.message : String(error);

    if (retryCount < MAX_RETRIES) {
      container.dataset.sdxRetry = String(retryCount + 1);
      console.warn(`${this.adapter.logPrefix} Mount failed for ${componentId}, retrying: ${message}`);
      setTimeout(() => {
        (container as ContainerWithFlags)._sdxMounting = false;
        void this.mount(container, ctx);
      }, RETRY_DELAY_MS);
      return;
    }

    reportMountCrash(container, componentId, error, retryCount, {
      logPrefix: this.adapter.logPrefix,
    });
  }
}
